import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useMemo, useState } from 'react';
import { brandColors } from 'types/colors';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import { SimpleControllerCard } from '../../components/Controllers/SimpleControllerCard';

const DevicesSelect = ({ open, onClose, buildingSensors, forcedBuildingId, selectedDevices, onSubmit, tooltipText }) => {
  const devices = Array.isArray(buildingSensors) ? buildingSensors : [];
  const [localSelectedIds, setLocalSelectedIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    const initialIds = Array.isArray(selectedDevices) ? selectedDevices : [];
    setLocalSelectedIds(initialIds);
  }, [open, selectedDevices]);

  const allFilteredDeviceIds = useMemo(() => [...new Set(devices.map(sensor => Number(sensor.id)))], [devices]);

  const toggleDeviceSelection = deviceId => {
    const normalizedId = Number(deviceId);
    setLocalSelectedIds(prev => {
      if (prev.includes(normalizedId)) {
        return prev.filter(id => id !== normalizedId);
      }
      return [...prev, normalizedId];
    });
  };

  const handleCancel = () => {
    setLocalSelectedIds([]);
    if (onClose) onClose();
  };

  const handleSubmit = () => {
    if (onSubmit) onSubmit(localSelectedIds);
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="select-devices">
      <DialogTitle>
        Collective Decision Mode
        <Tooltip
          title={tooltipText}
          arrow
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 300,
              },
            },
          }}>
          <IconButton size="small" onClick={event => event.stopPropagation()}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <Box sx={{ padding: '1rem' }}>
        <DevicesListFilter
          building={false}
          distributor={false}
          customer={false}
          status={false}
          hvacEquipment
          forcedBuildingId={forcedBuildingId}
        />
      </Box>

      <DialogContent sx={{ minHeight: '400px' }}>
        {devices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>No available devices</Typography>
          </Box>
        ) : (
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {devices.map(sensor => (
              <Grid item xs={12} sm={6} md={4} key={sensor.id}>
                <Box
                  onClick={() => toggleDeviceSelection(sensor.id)}
                  sx={{
                    position: 'relative',
                    opacity: localSelectedIds.includes(Number(sensor.id)) ? 1 : 0.6,
                    transition: 'opacity 0.2s',
                    cursor: 'pointer',
                    '&:hover': { opacity: 1 },

                    width: 'fit-content',
                    border: localSelectedIds.includes(Number(sensor.id)) ? `3px solid ${brandColors.BLUE}` : '3px solid #fff',
                    borderRadius: '6px',
                  }}>
                  <SimpleControllerCard
                    id={sensor.id}
                    name={sensor.name}
                    building_name={sensor.building_name ?? sensor.building}
                    floorNumber={sensor.floor_number}
                    area_name={sensor.area_name}
                    deviceType="sensor"
                    onCardClick={e => {
                      e.preventDefault();
                    }}
                    hoverAnimation={false}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}>
          <Box className="select">
            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                disabled={allFilteredDeviceIds.length === 0 || localSelectedIds.length === allFilteredDeviceIds.length}
                onClick={() => setLocalSelectedIds(allFilteredDeviceIds)}
                sx={{ color: '#000' }}>
                Select all
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={localSelectedIds.length === 0}
                onClick={() => setLocalSelectedIds([])}
                sx={{ color: '#000' }}>
                Unselect all
              </Button>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {localSelectedIds.length} device(s) selected
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DevicesSelect;
